import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { apiRequest } from "../../shared/api/client";
import { financeReportSchema, financeDetailSchema } from "../../../shared/account-finance/account-finance-contract";
import type { FinanceReport, FinanceDetail } from "../../../shared/account-finance/account-finance-contract";
import { filterFinanceAccounts } from "../../../shared/account-finance/account-finance-domain";

const accessSchema = z.object({ name: z.string(), company: z.string(), today: z.iso.date(), accountCount: z.number().int() });
const today = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul" }).format(new Date());

export function useAccountFinanceWorkspace(enabled = true, requestedCode = "") {
  const [date, setDate] = useState(today);
  const [version, setVersion] = useState(0);
  const [report, setReport] = useState<FinanceReport | null>(null);
  const [access, setAccess] = useState<z.infer<typeof accessSchema> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currency, setCurrency] = useState("TL");
  const [direction, setDirection] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedCode, setSelectedCode] = useState("");
  const [view, setView] = useState("invoices");
  const [pagination, setPagination] = useState({ key: "", page: 1 });
  const [detail, setDetail] = useState<FinanceDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(null); setReport(null);
    const signal = AbortSignal.any([controller.signal, AbortSignal.timeout(180_000)]);
    void Promise.all([
      apiRequest<unknown>("/api/account-finance/access", { signal }),
      enabled && requestedCode ? apiRequest<unknown>(`/api/account-finance/report?date=${date}&code=${encodeURIComponent(requestedCode)}`, { signal }) : Promise.resolve(null),
    ]).then(([rawAccess, rawReport]) => {
      if (controller.signal.aborted) return;
      const nextAccess = accessSchema.parse(rawAccess);
      const nextReport = rawReport === null ? null : financeReportSchema.parse(rawReport);
      setAccess(nextAccess); setReport(nextReport);
      if (nextReport?.scope === "SELECTED_ACCOUNT") { setCurrency(nextReport.accounts[0]?.currency ?? "TL"); setSearch(""); setDirection("all"); }
    }).catch((failure: unknown) => {
      if (!controller.signal.aborted) setError(failure instanceof z.ZodError ? "Cari raporu doğrulanamadı. Yeniden deneyin."
        : failure instanceof Error ? failure.message : "Cari raporu alınamadı.");
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [date, version, enabled, requestedCode]);

  const accounts = useMemo(() => filterFinanceAccounts(report?.accounts ?? [], { currency, direction, search }), [report, currency, direction, search]);
  const selected = accounts.find((row) => row.code === selectedCode) ?? accounts[0] ?? null;
  const detailKey = `${report?.id}:${selected?.code}:${view}`;
  const page = pagination.key === detailKey ? pagination.page : 1;

  useEffect(() => {
    const controller = new AbortController();
    setDetail(null); setDetailError(null);
    if (!report || !selected) { setDetailLoading(false); return () => controller.abort(); }
    setDetailLoading(true);
    void apiRequest<unknown>(`/api/account-finance/reports/${report.id}/accounts/${selected.code}?view=${view}&page=${page}`,
      { signal: AbortSignal.any([controller.signal, AbortSignal.timeout(15_000)]) })
      .then((raw) => { if (!controller.signal.aborted) setDetail(financeDetailSchema.parse(raw)); })
      .catch((failure: unknown) => { if (!controller.signal.aborted) setDetailError(failure instanceof Error ? failure.message : "Cari detayı alınamadı."); })
      .finally(() => { if (!controller.signal.aborted) setDetailLoading(false); });
    return () => controller.abort();
  }, [report, selected?.code, view, page]);

  const totals = useMemo(() => accounts.reduce((sum, account) => {
    const balance = BigInt(account.balance);
    return { receivable: sum.receivable + (balance > 0n ? balance : 0n),
      payable: sum.payable + (balance < 0n ? -balance : 0n), links: sum.links + account.matchingCount };
  }, { receivable: 0n, payable: 0n, links: 0 }), [accounts]);

  async function exportReport() {
    if (!report || exporting) return;
    setExporting(true); setError(null);
    try {
      const query = new URLSearchParams({ currency, direction, search });
      const response = await fetch(`/api/account-finance/reports/${report.id}/export?${query}`, { signal: AbortSignal.timeout(30_000), credentials: "same-origin" });
      if (!response.ok) throw new Error(response.status === 409 ? "Rapor süresi doldu. Bakiyeleri yenileyin." : "Excel dosyası alınamadı.");
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement("a"); link.href = url; link.download = `cari-finans-${date}.xlsx`;
      document.body.append(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (failure: unknown) { setError(failure instanceof Error ? failure.message : "Excel dosyası alınamadı."); }
    finally { setExporting(false); }
  }

  return { date, setDate, version, loading, report, access, error, accounts, totals,
    currency, setCurrency, direction, setDirection, search, setSearch, selected, setSelectedCode,
    view, setView, detail, detailLoading, detailError, page,
    setPage: (next: number) => setPagination({ key: detailKey, page: next }),
    refresh: () => setVersion((value) => value + 1), exporting, exportReport };
}
